import * as React from 'react';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import { DELETED_ENTITY_CLASS, useDeletedEntity } from '@/lib/useDeletedEntity';
import type { OrgFunction } from '@bliks-io/contracts';
import { Item, ItemMedia, ItemContent, ItemTitle, ItemDescription, cn } from '@bliks-io/ui';

interface TeamSuccessorItemProps {
  // A OrgFunction (either kind) — process successors reference the unified
  // org-structure model. Reads only `name` / `email` / `deletedAt`.
  team: OrgFunction;
  variant?: 'default' | 'outline' | 'muted';
}

/**
 * TeamSuccessorItem Component
 *
 * Displays a orgFunction/team successor using the v2 Item component.
 * Shows the entity icon, name, and email.
 * Soft-deleted entities render grayed out with a "(deleted)" suffix.
 */
export const TeamSuccessorItem = React.forwardRef<HTMLDivElement, TeamSuccessorItemProps>(
  ({ team, variant = 'outline' }, ref) => {
    const { isDeleted, deletedSuffix } = useDeletedEntity(team);
    return (
      <Item
        ref={ref}
        variant={variant}
        size="default"
        className={cn('pr-8', isDeleted && DELETED_ENTITY_CLASS)}
      >
        <ItemMedia variant="icon">
          <ENTITY_ICONS.team className="h-4 w-4" />
        </ItemMedia>
        <ItemContent>
          <ItemTitle>{team.name + deletedSuffix}</ItemTitle>
          {team.email && <ItemDescription>{team.email}</ItemDescription>}
        </ItemContent>
      </Item>
    );
  }
);

TeamSuccessorItem.displayName = 'TeamSuccessorItem';
